import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, DownloadSimple, File as FileIcon } from "@phosphor-icons/react";

const CATS = {
  contract: "Contract", oferta: "Ofertă", factura: "Factură", proforma: "Proformă",
  comanda: "Comandă", aviz: "Aviz", cmr: "CMR", desen: "Desen tehnic",
  fisa_proiect: "Fișă proiect", pv: "Proces-verbal", certificat: "Certificat",
  dovada: "Dovadă încasare", nota_santier: "Notă șantier", email: "Email",
  email_atasament: "Atașament email", altul: "Altul",
};

export default function DocumentSearch() {
  const [f, setF] = useState({ q: "", code: "", uploader: "", doc_type: "", client: "", date_from: "", date_to: "" });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const run = async () => {
    setBusy(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => (v || "").trim()));
      const { data } = await api.get("/documents-search", { params });
      setResults(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reset = () => { setF({ q: "", code: "", uploader: "", doc_type: "", client: "", date_from: "", date_to: "" }); setResults(null); };

  return (
    <div className="space-y-6" data-testid="document-search-page">
      <div>
        <h1 className="font-head text-2xl font-bold">Căutare documente</h1>
        <p className="text-sm text-muted-foreground">Caută în dosarul digital al tuturor proiectelor: denumire, cod proiect, utilizator, tip, client și perioadă.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Denumire document" data-testid="ds-q" />
          <Input value={f.code} onChange={(e) => set("code", e.target.value)} placeholder="Cod proiect (ex: HGS 1904)" data-testid="ds-code" />
          <Input value={f.client} onChange={(e) => set("client", e.target.value)} placeholder="Client / beneficiar" data-testid="ds-client" />
          <Input value={f.uploader} onChange={(e) => set("uploader", e.target.value)} placeholder="Utilizator (nume/email)" data-testid="ds-uploader" />
          <Input value={f.doc_type} onChange={(e) => set("doc_type", e.target.value)} placeholder="Tip document (ex: contract, factura)" data-testid="ds-doctype" />
          <div className="flex items-center gap-2">
            <Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="ds-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="ds-to" />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button onClick={run} disabled={busy} data-testid="ds-search-btn"><MagnifyingGlass size={16} className="mr-1.5" /> {busy ? "Se caută…" : "Caută"}</Button>
          <Button variant="outline" onClick={reset} data-testid="ds-reset-btn">Resetează</Button>
        </div>
      </div>

      {results && (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="ds-results">
          <p className="mb-3 text-sm text-muted-foreground">{results.total} rezultate{results.total > 500 ? " (primele 500 afișate)" : ""}</p>
          <div className="space-y-2">
            {(results.results || []).map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`ds-row-${d.id}`}>
                <FileIcon size={18} className="shrink-0 text-blue-400" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{d.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {d.project_code || "—"} · {d.project_client || "—"} · {fmtDate(d.created_at)} · {d.uploaded_by_email || d.uploaded_by_name || "—"}
                  </div>
                </div>
                <span className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] text-muted-foreground">{CATS[d.category] || d.category}</span>
                <a href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" data-testid={`ds-open-${d.id}`}><DownloadSimple size={17} /></a>
              </div>
            ))}
            {(results.results || []).length === 0 && <p className="text-sm text-muted-foreground">Niciun document găsit pentru criteriile alese.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
